import React, { ReactElement } from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import CircularProgress from '@mui/material/CircularProgress';
import { FormattedMessage, formatTranslation } from 'intl';
import { CommonTranslationKeys, WorkflowTranslationKeys } from 'intl/keys';
import { useIntl } from 'react-intl';
import ViewWrapper from 'components/core/Layout/ViewWrapper';
import useIsMobile from 'hooks/useIsMobile';
import { MemoButtonContained } from 'components/general/ButtonContained/ButtonContained';
import {
  FORM_SECTION_CARD_SX,
  FORM_SECTION_DESCRIPTION_SX,
  FORM_SECTION_TITLE_SX,
} from 'components/template/wizard/FormSection/styles';
import { StageListPanel } from '../../components/StageListPanel';
import type { WorkflowEditorProps } from './types';
import {
  pageShellSx,
  loaderBoxSx,
  footerBarSx,
  footerInnerSx,
  getMainContentSx,
  nameFieldSx,
  footerSpacerSx,
} from './styles';

export function WorkflowEditorPresentational({
  workflow,
  workflowName,
  localStages,
  isLocked,
  isLockedWithoutApprovers,
  availableApprovers,
  workflowSaveLoading,
  showNameErrors,
  onChangeWorkflowName,
  onSaveWorkflow,
  onAddStage,
  onDeleteStage,
  onUpdateStage,
  onStagesReorder,
  onGoBack,
}: WorkflowEditorProps): ReactElement {
  const intl = useIntl();
  const isMobile = useIsMobile();

  const posting = workflowSaveLoading;
  const hasEmptyStageNames = localStages.some((s) => s.name.trim().length === 0);
  const saveDisabled = !workflowName.trim() || workflowSaveLoading || hasEmptyStageNames;
  const disabled = isLocked || workflowSaveLoading;
  const approversDisabled = workflowSaveLoading || isLockedWithoutApprovers;

  const stagesDescription = isLocked
    ? isLockedWithoutApprovers
      ? formatTranslation(
          intl,
          WorkflowTranslationKeys.creatorStagesDescriptionLockedNoApprovers
        )
      : formatTranslation(
          intl,
          WorkflowTranslationKeys.creatorStagesDescriptionLockedWithApprovers
        )
    : formatTranslation(intl, WorkflowTranslationKeys.creatorStagesDescription);

  if (!workflow) {
    return (
      <ViewWrapper>
        <Box sx={pageShellSx}>
          <Box sx={loaderBoxSx}>
            <CircularProgress />
          </Box>
        </Box>
      </ViewWrapper>
    );
  }

  return (
    <>
      <ViewWrapper>
        <Box sx={pageShellSx}>
          <Box sx={getMainContentSx(isMobile)} data-qa="workflow-creator-page">
            <Box sx={FORM_SECTION_CARD_SX}>
              <Typography component="h2" sx={FORM_SECTION_TITLE_SX}>
                <FormattedMessage id={WorkflowTranslationKeys.creatorBasicInfoTitle} />
              </Typography>
              {!isLocked ? (
                <Typography component="p" sx={FORM_SECTION_DESCRIPTION_SX}>
                  <FormattedMessage id={WorkflowTranslationKeys.creatorBasicInfoDescription} />
                </Typography>
              ) : null}
              <TextField
                fullWidth
                label={formatTranslation(intl, WorkflowTranslationKeys.configurationNameLabel,)}
                value={workflowName}
                onChange={(e) => onChangeWorkflowName(e.target.value)}
                disabled={workflowSaveLoading}
                size="small"
                required
                sx={{ ...nameFieldSx, ...(isLocked ? { mt: 2 } : {}) }}
                inputProps={{ 'data-qa': 'workflow-creator-name' }}
              />
            </Box>

            <Box sx={FORM_SECTION_CARD_SX}>
              <Typography component="h2" sx={FORM_SECTION_TITLE_SX}>
                <FormattedMessage id={WorkflowTranslationKeys.creatorStagesTitle} />
              </Typography>
              <Typography component="p" sx={FORM_SECTION_DESCRIPTION_SX}>
                {stagesDescription}
              </Typography>

              <StageListPanel
                stages={localStages}
                isLocked={isLocked}
                disabled={disabled}
                approversDisabled={approversDisabled}
                showNameErrors={showNameErrors}
                availableApprovers={availableApprovers}
                onStagesReorder={onStagesReorder}
                onAddStage={onAddStage}
                onUpdateStage={onUpdateStage}
                onDeleteStage={(id) => void onDeleteStage(id)}
              />
            </Box>

            <Box sx={footerSpacerSx} />
          </Box>
        </Box>
      </ViewWrapper>

      <Box sx={footerBarSx} data-qa="workflow-creator-footer">
        <Stack direction="row" justifyContent="space-between" sx={footerInnerSx}>
          <Button
            onClick={onGoBack}
            disabled={posting}
            data-qa="workflow-creator-cancel"
          >
            {formatTranslation(intl, CommonTranslationKeys.cancel)}
          </Button>
          <MemoButtonContained
            onClick={() => void onSaveWorkflow()}
            disabled={saveDisabled}
            endIcon={
              workflowSaveLoading ? (
                <CircularProgress size={16} color="inherit" />
              ) : undefined
            }
            data-qa="workflow-creator-save"
          >
            {formatTranslation(intl, WorkflowTranslationKeys.creatorSave)}
          </MemoButtonContained>
        </Stack>
      </Box>
    </>
  );
}
